import React, { useState, useEffect } from 'react';
import { Trash2, Eye, Printer, Plus, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface WeeklyReport {
  id: number;
  week_start_date: string;
  week_end_date: string;
  total_sales: number;
  total_customers: number;
  avg_daily_sales: number;
  ai_generated_report: string;
  created_at: string;
}

interface MonthlyReport {
  id: number;
  year: number;
  month: number;
  total_sales: number;
  total_customers: number;
  avg_daily_sales: number;
  best_day_sales: number;
  best_day_date: string;
  ai_generated_report: string;
  created_at: string;
}

type ReportType = 'weekly' | 'monthly';

const ReportList: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ReportType>('weekly');
  const [weeklyReports, setWeeklyReports] = useState<WeeklyReport[]>([]);
  const [monthlyReports, setMonthlyReports] = useState<MonthlyReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState<WeeklyReport | MonthlyReport | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());

  useEffect(() => {
    fetchReports();
  }, [activeTab, selectedYear]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      if (activeTab === 'weekly') {
        const res = await fetch(`/api/daily-reports/weekly/list?limit=100`);
        const data = await res.json();
        if (data.success) {
          setWeeklyReports(data.data);
        }
      } else {
        const res = await fetch(`/api/daily-reports/monthly/list?year=${selectedYear}&limit=12`);
        const data = await res.json();
        if (data.success) {
          setMonthlyReports(data.data);
        }
      }
    } catch (error) {
      console.error('レポート取得エラー:', error);
      alert('レポート取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, type: ReportType) => {
    if (!confirm(`この${type === 'weekly' ? '週報' : '月報'}を削除しますか？`)) {
      return;
    }

    try {
      const res = await fetch(`/api/daily-reports/${type}/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json();

      if (data.success) {
        alert(data.message);
        fetchReports();
      } else {
        alert('削除に失敗しました');
      }
    } catch (error) {
      console.error('削除エラー:', error);
      alert('削除に失敗しました');
    }
  };

  const handleView = (report: WeeklyReport | MonthlyReport) => {
    setSelectedReport(report);
  };

  const handlePrint = (report: WeeklyReport | MonthlyReport) => {
    setSelectedReport(report);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const isWeeklyReport = (report: WeeklyReport | MonthlyReport): report is WeeklyReport => {
    return 'week_start_date' in report;
  };

  const reports = activeTab === 'weekly' ? weeklyReports : monthlyReports;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-6 print:p-0 print:bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden print:shadow-none">
          {/* ヘッダー */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-8 print:hidden">
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => navigate('/reports/calendar')}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-3xl font-bold">📊 売上レポート管理</h1>
                <p className="text-sm opacity-90 mt-1">週報・月報の一覧表示と管理</p>
              </div>
            </div>
          </div>

          {/* タブ */}
          <div className="flex bg-gray-100 border-b-2 border-gray-200 print:hidden">
            <button
              onClick={() => setActiveTab('weekly')}
              className={`flex-1 py-4 px-6 font-semibold transition-all ${
                activeTab === 'weekly'
                  ? 'bg-white text-blue-600 border-b-3 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              📊 週報
            </button>
            <button
              onClick={() => setActiveTab('monthly')}
              className={`flex-1 py-4 px-6 font-semibold transition-all ${
                activeTab === 'monthly'
                  ? 'bg-white text-purple-600 border-b-3 border-purple-600'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              📅 月報
            </button>
          </div>

          {/* フィルター・検索エリア */}
          <div className="p-8 bg-gray-50 border-b border-gray-200 print:hidden">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">年</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  {[2025, 2024, 2023].map(year => (
                    <option key={year} value={year}>{year}年</option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => navigate('/reports/calendar')}
                className="ml-auto bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all flex items-center gap-2"
              >
                <Plus size={20} />
                新規作成
              </button>
            </div>
          </div>

          {/* コンテンツ */}
          <div className="p-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">読み込み中...</p>
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">📊</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {activeTab === 'weekly' ? '週報' : '月報'}がまだありません
                </h3>
                <p className="text-gray-500">
                  「新規作成」ボタンから最初の{activeTab === 'weekly' ? '週報' : '月報'}を作成しましょう
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {reports.map((report) => (
                  <div
                    key={report.id}
                    className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-gray-900">
                          {isWeeklyReport(report)
                            ? `${new Date(report.week_start_date).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long' })} 週報`
                            : `${report.year}年${report.month}月`}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold print:hidden ${
                          activeTab === 'weekly' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {activeTab === 'weekly' ? '週報' : '月報'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 print:hidden">
                        作成日: {new Date(report.created_at).toLocaleString('ja-JP')}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">💰</span>
                        <div>
                          <div className="text-xs text-gray-500">合計売上</div>
                          <div className="text-base font-bold">¥{report.total_sales.toLocaleString()}</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-xl">👥</span>
                        <div>
                          <div className="text-xs text-gray-500">合計客数</div>
                          <div className="text-base font-bold">{report.total_customers}人</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-xl">📈</span>
                        <div>
                          <div className="text-xs text-gray-500">平均売上/日</div>
                          <div className="text-base font-bold">¥{report.avg_daily_sales.toLocaleString()}</div>
                        </div>
                      </div>

                      {!isWeeklyReport(report) && (
                        <div className="flex items-center gap-2">
                          <span className="text-xl">🏆</span>
                          <div>
                            <div className="text-xs text-gray-500">最高売上日</div>
                            <div className="text-base font-bold">
                              {new Date(report.best_day_date).getDate()}日 (¥{report.best_day_sales.toLocaleString()})
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <div className="text-xs text-gray-600 mb-1">📅 期間</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {isWeeklyReport(report)
                          ? `${report.week_start_date} 〜 ${report.week_end_date}`
                          : `${report.year}年${report.month}月`}
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4 border-t border-gray-100 print:hidden">
                      <button
                        onClick={() => handleView(report)}
                        className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                      >
                        <Eye size={18} />
                        表示
                      </button>
                      <button
                        onClick={() => handlePrint(report)}
                        className="flex-1 bg-purple-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
                      >
                        <Printer size={18} />
                        印刷
                      </button>
                      <button
                        onClick={() => handleDelete(report.id, activeTab)}
                        className="bg-red-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* レポート詳細モーダル */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 print:contents">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8 print:shadow-none print:max-w-full print:max-h-full print:p-8 print:m-0 print:border-0 print:rounded-none">
            <button
              onClick={() => setSelectedReport(null)}
              className="float-right text-gray-500 hover:text-gray-700 print:hidden text-2xl"
            >
              ✕
            </button>

            <div className="clear-both">
              <div className={`border-b-4 pb-4 mb-6 ${
                isWeeklyReport(selectedReport) ? 'border-blue-500' : 'border-purple-500'
              }`}>
                <h2 className={`text-2xl font-bold ${
                  isWeeklyReport(selectedReport) ? 'text-blue-600' : 'text-purple-600'
                }`}>
                  {isWeeklyReport(selectedReport) ? '📊 和とん週報' : '📅 和とん月報'}
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  {isWeeklyReport(selectedReport)
                    ? `${selectedReport.week_start_date} 〜 ${selectedReport.week_end_date}`
                    : `${selectedReport.year}年${selectedReport.month}月`}
                </p>
                <div className="mt-2 text-sm text-gray-700 flex flex-wrap gap-4">
                  <span>💰 合計売上: ¥{selectedReport.total_sales.toLocaleString()}</span>
                  <span>👥 合計客数: {selectedReport.total_customers}人</span>
                  <span>📈 平均売上: ¥{selectedReport.avg_daily_sales.toLocaleString()}/日</span>
                  {!isWeeklyReport(selectedReport) && (
                    <span>🏆 最高売上: {new Date(selectedReport.best_day_date).getDate()}日 (¥{selectedReport.best_day_sales.toLocaleString()})</span>
                  )}
                </div>
              </div>

              <div className="prose max-w-none whitespace-pre-wrap">
                {selectedReport.ai_generated_report}
              </div>

              <div className="mt-6 flex gap-3 print:hidden">
                <button
                  onClick={() => window.print()}
                  className={`flex-1 text-white py-2 px-4 rounded hover:opacity-90 flex items-center justify-center gap-2 ${
                    isWeeklyReport(selectedReport) ? 'bg-blue-500' : 'bg-purple-500'
                  }`}
                >
                  🖨️ 印刷
                </button>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportList;